import React from 'react';
import {
  Flex,
  Text,
  Heading,
  Button,
  Box,
  Stack,
  Icon,
  Stat,
  StatLabel,
  StatNumber,
  useColorModeValue,
} from '@chakra-ui/react';
import { AddIcon } from '@chakra-ui/icons';
import { FaWallet, FaCalendarAlt, FaHistory } from 'react-icons/fa';
import { IconType } from 'react-icons';
import { Trans } from '@lingui/macro';

interface MonthlyData {
  month: string;
  vehicleCount: number;
  amount: string;
}

interface AccountingCardProps {
  balance: string;
  currentMonth: MonthlyData;
  previousMonth: MonthlyData;
  onAddBalance: () => void;
}

interface MonthItemProps {
  icon: IconType | React.ComponentType;
  month: string;
  count: number;
  amount: string;
  labelColor: string;
  valueColor: string;
  accentColor?: string;
}

// Helper component for month items
const MonthItem: React.FC<MonthItemProps> = ({
  icon,
  month,
  count,
  amount,
  labelColor,
  valueColor,
  accentColor = 'blue.500',
}) => {
  return (
    <Flex justify="space-between" align="center">
      <Flex align="center">
        <Icon as={icon} color={accentColor} mr={2} />
        <Text color={labelColor} fontWeight="medium">
          {month}
        </Text>
      </Flex>
      <Flex align="center" fontWeight="bold" color={valueColor}>
        <Text mr={2}>{count} Araç,</Text>
        <Text>{amount}</Text>
      </Flex>
    </Flex>
  );
};

export const AccountingCard: React.FC<AccountingCardProps> = ({
  balance,
  currentMonth,
  previousMonth,
  onAddBalance,
}) => {
  const headingColor = useColorModeValue('gray.800', 'white');
  const accentColor = useColorModeValue('blue.500', 'blue.300');
  const labelColor = useColorModeValue('gray.600', 'gray.400');
  const valueColor = useColorModeValue('gray.800', 'white');
  const bgColor = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.700');
  const buttonBg = useColorModeValue('green.500', 'green.600');
  const buttonHoverBg = useColorModeValue('green.600', 'green.700');

  // Check if balance is negative by looking for "-" character
  const isNegativeBalance = balance.includes('-');
  const balanceBg = useColorModeValue(
    isNegativeBalance ? 'red.50' : 'green.50',
    isNegativeBalance ? 'red.900' : 'green.900',
  );
  const balanceBorderColor = isNegativeBalance ? 'red.500' : 'green.500';
  const balanceTextColor = useColorModeValue(
    isNegativeBalance ? 'red.600' : valueColor,
    isNegativeBalance ? 'red.300' : valueColor,
  );

  return (
    <Box height="100%" width="100%" display="flex" flexDirection="column" minHeight="300px">
      <Flex mb={4} align="center">
        <Icon as={FaWallet} color={accentColor} mr={2} boxSize={5} />
        <Heading size="md" color={headingColor}>
          <Trans>Muhasebe</Trans>
        </Heading>
      </Flex>

      <Box
        flex="1"
        bg={bgColor}
        borderWidth="1px"
        borderRadius="lg"
        borderColor={borderColor}
        position="relative"
        overflow="hidden"
        boxShadow="sm"
        p={5}
        _before={{
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          width: '4px',
          height: '100%',
          bg: accentColor,
        }}
      >
        <Box
          mb={5}
          bg={balanceBg}
          p={4}
          borderRadius="md"
          borderLeftWidth="4px"
          borderColor={balanceBorderColor}
          transition="all 0.3s ease"
        >
          <Stat>
            <StatLabel color={labelColor} fontWeight="medium">
              <Trans>Bakiye</Trans>
            </StatLabel>
            <StatNumber fontSize="2xl" color={balanceTextColor}>
              {balance}
            </StatNumber>
          </Stat>
        </Box>

        <Stack spacing={5} mb={6}>
          <MonthItem
            icon={FaCalendarAlt}
            month={currentMonth.month}
            count={currentMonth.vehicleCount}
            amount={currentMonth.amount}
            labelColor={labelColor}
            valueColor={valueColor}
            accentColor="teal.500"
          />

          <MonthItem
            icon={FaHistory}
            month={previousMonth.month}
            count={previousMonth.vehicleCount}
            amount={previousMonth.amount}
            labelColor={labelColor}
            valueColor={valueColor}
            accentColor="purple.500"
          />
        </Stack>

        <Button
          leftIcon={<AddIcon />}
          w="100%"
          size="md"
          color="white"
          bg={buttonBg}
          _hover={{ bg: buttonHoverBg }}
          borderRadius="md"
          fontWeight="semibold"
          boxShadow="sm"
          onClick={onAddBalance}
        >
          Bakiye Yükle
        </Button>
      </Box>
    </Box>
  );
};
